'use client';

import { AUTH_PROVIDER, SESSION_STATUS } from '@/constants/auth';
import { ENTITY_TAB } from '@/constants/entityTabs';
import { createElement, type ReactNode, useEffect, useMemo } from 'react';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import { signIn, useSession } from 'next-auth/react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import usePublicSearchContext from '@/publicSearch/store/usePublicSearchContext';
import { useIsI4Society } from '@/hooks/publicSearch/useIsI4Society';
import { useSearchType, SearchType } from '@/hooks/publicSearch/useSearchType';
import { buildSocietySearchHref } from '@/lib/publicSearchPaths';
import {
  I4_SEARCH_TYPES,
  SALE_CATALOGUE_CHILD_SEARCH_TYPES,
  SALE_ID_SEARCH_TYPES,
  SEARCH_TYPE,
} from '@/constants/searchType';
import { SEARCH_PARAM } from '@/constants/searchParams';
import { SOCIETY_SETTING_TYPE } from '@/constants/societySettings';
import useFilterStore from '@/publicSearch/store/useFilterStore';
import useGridStore from '@/publicSearch/store/useGridStore';
import useSearchStore from '@/publicSearch/store/useSearchStore';
import { useI4Links, useI4RedirectUrl } from '@/publicSearch/graphql/i4/hooks';
import type { UserI4LinkDto } from '@/publicSearch/graphql/i4/types';
export type SocietySearchTab = {
  icon?: ReactNode;
  label: string;
  value: string;
};

const defaultTabs: SocietySearchTab[] = [
  { label: 'Animal Enquiry', value: SEARCH_TYPE.ANIMAL },
  { label: 'Member Enquiry', value: SEARCH_TYPE.MEMBERS },
  { label: 'Sale Catalogues', value: SEARCH_TYPE.SALE_CATALOGUES },
];

type TabConfig = {
  title: string;
  path: string;
  enabled: boolean;
  order: number;
};

type TabSetting = {
  value?: string | null;
  societySettingType?: string;
};

const settingTypeToTabValue: Record<string, string> = {
  ANIMAL_TAB: SEARCH_TYPE.ANIMAL,
  MEMBERS_TAB: SEARCH_TYPE.MEMBERS,
  EBV_TAB: ENTITY_TAB.EBVS,
  SALES_TAB: SEARCH_TYPE.SALE_CATALOGUES,
  MATING_TAB: SEARCH_TYPE.MATING,
  SEMEN_CATALOG_TAB: SEARCH_TYPE.SALE_CATALOGUES,
  DOWNLOADS_TAB: SEARCH_TYPE.DOWNLOADS,
  ONLINE_REGISTRATIONS_TAB: SEARCH_TYPE.ONLINE_REGISTRATIONS,
};

const TAB_JSON_SETTING_TYPES = new Set(Object.keys(settingTypeToTabValue));

function isLikelyTabConfigJson(value: string) {
  const t = value.trimStart();
  return t.startsWith('{') || t.startsWith('[');
}

const SOCIETY_SEARCH_PATH = /^\/[^/]+\/(animals|members|sales|external)(\/|$)/;

const pathToSettingType: Record<string, string> = {
  '/animal': 'ANIMAL_TAB',
  '/member': 'MEMBERS_TAB',
  '/ebvs': 'EBV_TAB',
  '/sales': 'SALES_TAB',
  '/mating': 'MATING_TAB',
  '/semen-catalog': 'SEMEN_CATALOG_TAB',
  '/downloads': 'DOWNLOADS_TAB',
  '/online-registrations': 'ONLINE_REGISTRATIONS_TAB',
};

export function useSocietySearchTabsModel() {
  const pathname = usePathname();
  const society = usePublicSearchContext((context) => context.society);
  const isI4Society = useIsI4Society();
  const { status } = useSession();
  const { clearFilter } = useFilterStore();
  const { paginationModel, setGridSortModel, setPaginationModel } =
    useGridStore();
  const { setBasicSearch } = useSearchStore();
  const searchType = useSearchType();
  const router = useRouter();
  const searchParams = useSearchParams();
  const societyCode = society.societyCode ?? '';
  const isAuthenticated = status === SESSION_STATUS.AUTHENTICATED;
  const { data: linksData } = useI4Links(!isAuthenticated || !societyCode);
  const links = linksData?.i4Links ?? [];
  const linkForSociety = links.find(
    (l: UserI4LinkDto) => l.societyCode === societyCode,
  );
  const isLinked = !!linkForSociety;
  const { data: redirectUrlData } = useI4RedirectUrl(
    {
      societyCode,
      runMode: 'IO',
      societyId: linkForSociety?.societyId,
    },
    !isAuthenticated || !isLinked,
  );
  const i4RedirectUrl = redirectUrlData?.i4RedirectUrl;

  let tabValue = searchType;
  if (SALE_CATALOGUE_CHILD_SEARCH_TYPES.includes(searchType)) {
    tabValue = SEARCH_TYPE.SALE_CATALOGUES;
  }

  const tabs = useMemo(() => {
    if (!society.settings || society.loading) {
      return defaultTabs;
    }

    const tabSettings = society.settings.filter((setting) => {
      if (!setting.value) return false;
      if (setting.societySettingType === SOCIETY_SETTING_TYPE.SOCIETY_LOGO)
        return false;
      if (
        setting.societySettingType &&
        TAB_JSON_SETTING_TYPES.has(setting.societySettingType)
      ) {
        return true;
      }
      if (!setting.societySettingType && isLikelyTabConfigJson(setting.value)) {
        return true;
      }
      return false;
    }) as Array<TabSetting & { societySettingType?: string }>;

    if (tabSettings.length === 0) {
      return defaultTabs;
    }

    const processedTabs: Array<SocietySearchTab & { order: number }> = [];

    for (const setting of tabSettings) {
      try {
        const config: TabConfig = JSON.parse(setting.value!);
        if (!config.enabled) {
          continue;
        }
        let settingType = setting.societySettingType;
        if (!settingType && config.path) {
          settingType = pathToSettingType[config.path];
        }
        const mapped = settingType
          ? settingTypeToTabValue[settingType]
          : undefined;
        if (!mapped) {
          continue;
        }
        processedTabs.push({
          label: config.title || '',
          value: mapped,
          order: config.order ?? 999,
        });
      } catch (error) {
        console.warn(
          `Failed to parse tab setting for ${setting.societySettingType}:`,
          error,
        );
      }
    }

    processedTabs.sort((a, b) => a.order - b.order);
    const seen = new Set<string>();
    const deduped = processedTabs.filter((tab) => {
      if (seen.has(tab.value)) return false;
      seen.add(tab.value);
      return true;
    });

    const i4OnlyValues = new Set(I4_SEARCH_TYPES);
    let filtered = isI4Society
      ? deduped
      : deduped.filter((tab) => !i4OnlyValues.has(tab.value));

    const isEbvEnabled = society.isEbvEnabled ?? true;
    if (!isEbvEnabled) {
      filtered = filtered.filter(
        (tab) =>
          tab.value !== ENTITY_TAB.EBVS && tab.value !== SEARCH_TYPE.MATING,
      );
    }

    if (filtered.length === 0) {
      return defaultTabs;
    }

    return filtered.map(({ order: _order, ...tab }) => {
      if (!isAuthenticated && i4OnlyValues.has(tab.value)) {
        return {
          ...tab,
          icon: createElement(LockOutlinedIcon, { sx: { fontSize: 16 } }),
        };
      }
      return tab;
    });
  }, [
    society.settings,
    society.loading,
    society.isEbvEnabled,
    isI4Society,
    isAuthenticated,
  ]);

  const lockedTabs = useMemo(
    () => new Set(tabs.filter((t) => t.icon != null).map((t) => t.value)),
    [tabs],
  );

  function handleTabChange(value: string) {
    if (lockedTabs.has(value)) {
      signIn(AUTH_PROVIDER);
      return;
    }
    if (
      value === SEARCH_TYPE.ONLINE_REGISTRATIONS &&
      isAuthenticated &&
      isLinked &&
      i4RedirectUrl
    ) {
      window.open(i4RedirectUrl, '_blank', 'noopener,noreferrer');
      return;
    }
    const orgCode = pathname?.split('/')[1] || society.societyCode || '';
    const nextParams = new URLSearchParams(searchParams?.toString() ?? '');
    nextParams.delete(SEARCH_PARAM.SEARCH_TYPE);
    if (!SALE_ID_SEARCH_TYPES.includes(value)) {
      nextParams.delete(SEARCH_PARAM.SALE_ID);
    }
    const nextHref = buildSocietySearchHref(
      orgCode,
      value as SearchType,
      nextParams,
    );

    // Navigate first so tab clicks still route even if downstream store updates fail.
    router.push(nextHref);
    clearFilter();
    setBasicSearch('');
    setGridSortModel([]);
    setPaginationModel({ page: 0, pageSize: paginationModel.pageSize });
  }

  useEffect(() => {
    const validTabValues = tabs.map((tab) => tab.value) as SearchType[];
    const searchTypes: SearchType[] = [
      ...validTabValues,
      SEARCH_TYPE.SALE_CATALOGUES_DETAIL_ANIMALS,
      SEARCH_TYPE.SALE_CATALOGUES_NOTES,
      SEARCH_TYPE.SEMEN_CATALOGUES_DETAIL_ANIMALS,
      SEARCH_TYPE.DOWNLOADS,
      SEARCH_TYPE.ONLINE_REGISTRATIONS,
    ];
    if (searchTypes.includes(searchType)) return;
    const defaultTab = tabs.length > 0 ? tabs[0]!.value : SEARCH_TYPE.ANIMAL;
    handleTabChange(defaultTab);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const onSocietySearchRoute = SOCIETY_SEARCH_PATH.test(pathname);
  /** Only block the header on missing org context — not while settings load (avoids skeleton→tabs swap + layout jump). */
  const societyHeaderTabsLoading = onSocietySearchRoute && !society.orgId;

  return {
    tabs,
    tabValue,
    handleTabChange,
    pathname,
    society,
    societyHeaderTabsLoading,
  };
}
